import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import { PropTypes } from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Checkbox, CheckedState } from '@ge/components/checkbox';
import { Loader } from '@ge/components/loader';
import { Tbody, Td, Tr } from '@ge/components/table';
import { useTableFactories } from '@ge/components/table/use-table-factories';
import { EntityType, Placeholders } from '@ge/models/constants';
import { killEventPropagation } from '@ge/shared/util';

import { useReportScope } from '../../../hooks/use-report-scope';
import { ReportWidgetTable } from '../../tables/report-widget-table';

import {
  ReferencePeriodTableColumns,
  referencePeriodContractTableDefs,
  referencePeriodSiteTableDefs,
} from './reference-period-table-cols';

dayjs.extend(isoWeek);

const StyledCheckbox = styled.div`
  label {
    display: inline-block;
    margin-top: 2px;
  }
  span {
    margin-left: 0;
  }
`;

const IncludeHeader = styled.span`
  color: ${({ theme }) => theme.createReport.widget.manuallyAdjustedEvents.includeTextColor};
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  ${({ summaryColumn }) =>
    summaryColumn &&
    `
    ${Tbody} ${Tr} ${Td}:last-of-type {
      font-weight: 500;
    }
  `}

  ${({ theme }) =>
    `${Tbody} ${Tr}:last-of-type ${Td} {
      border-top: 1px solid ${theme.createReport.widget.productionTable.rowBorderColor};
      font-weight: 700;
    }
  `}
`;

export const ReferencePeriodTableWidget = ({
  name,
  columns,
  data = [],
  isEditing,
  isInteractive,
  onRowSelectionChange,
  savedEntries,
}) => {
  const { t, ready } = useTranslation(['reporting.widgets']);
  // initialize all rows being selected
  const [rowsSelected, setRowsSelected] = useState(data.map(({ id }) => id));
  const { scopeType } = useReportScope();
  // console.log('data', data);

  useEffect(() => {
    onRowSelectionChange(rowsSelected);
  }, [onRowSelectionChange, rowsSelected]);

  useEffect(() => {
    setRowsSelected(savedEntries);
  }, [savedEntries]);

  /**
   * Render custom cells for specific columns
   */
  const customHeaderFn = useCallback(
    (columnKey) => {
      switch (columnKey) {
        case ReferencePeriodTableColumns.SELECTED:
          return (
            <IncludeHeader>
              {t('manually_adjusted_events.columns.include', 'Include')}
            </IncludeHeader>
          );
        default:
          return null;
      }
    },
    [t],
  );

  const toggleCheckedRow = useCallback(
    (e, rowId) => {
      if (rowsSelected.includes(rowId)) {
        setRowsSelected(rowsSelected.filter((row) => row !== rowId));
      } else {
        setRowsSelected([...rowsSelected, rowId]);
      }
    },
    [rowsSelected],
  );

  const customCellFn = useCallback(
    (columnKey, cellValue) => {
      switch (columnKey) {
        case ReferencePeriodTableColumns.SELECTED:
          return (
            <StyledCheckbox
              onClick={(e) => {
                killEventPropagation(e);
                e.preventDefault();
                toggleCheckedRow(e, cellValue);
              }}
            >
              <Checkbox
                checkState={
                  rowsSelected.includes(cellValue) ? CheckedState.CHECKED : CheckedState.UNCHECKED
                }
                label={''}
              />
            </StyledCheckbox>
          );
        default:
          return cellValue;
      }
    },
    [rowsSelected, toggleCheckedRow],
  );

  const cellValueMapFn = useCallback(
    (cellValue) => {
      return {
        [ReferencePeriodTableColumns.SELECTED]: cellValue.id || Placeholders.DASH,
        [ReferencePeriodTableColumns.REFERENCE_PERIOD]:
          cellValue.referencePeriod === ReferencePeriodTableColumns.REFERENCE_PERIOD_TOTAL
            ? t('reference_table.reference_period_totals', 'Reference Period Totals')
            : cellValue.referencePeriod,
        [ReferencePeriodTableColumns.AVAILABLE_TIME]:
          cellValue.referencePeriod === ReferencePeriodTableColumns.REFERENCE_PERIOD_TOTAL
            ? rowsSelected
                .reduce((sum, rowId) => {
                  const row = data[rowId-1];
                  return sum + (parseFloat(row?.availableTime) || 0);
                }, 0)
                .toFixed(2)
            : cellValue.availableTime
            ? cellValue.availableTime
            : Placeholders.DASH,
        [ReferencePeriodTableColumns.UNAVAILABLE_TIME]:
          cellValue.referencePeriod === ReferencePeriodTableColumns.REFERENCE_PERIOD_TOTAL
            ? rowsSelected
                .reduce((sum, rowId) => {
                  const row = data[rowId-1];
                  return sum + (parseFloat(row?.unavailableTime) || 0);
                }, 0)
                .toFixed(2)
            : cellValue.unavailableTime
            ? cellValue.unavailableTime
            : Placeholders.DASH,
        [ReferencePeriodTableColumns.PRODUCTION]:
          cellValue.referencePeriod === ReferencePeriodTableColumns.REFERENCE_PERIOD_TOTAL
            ? rowsSelected
                .reduce((sum, rowId) => {
                  const row = data[rowId-1];
                  return sum + (parseFloat(row?.actualProduction) || 0);
                }, 0)
                .toFixed(2)
            : cellValue.actualProduction
            ? cellValue.actualProduction
            : Placeholders.DASH,
        [ReferencePeriodTableColumns.LOST_PRODUCTION]:
          cellValue.referencePeriod === ReferencePeriodTableColumns.REFERENCE_PERIOD_TOTAL
            ? rowsSelected
                .reduce((sum, rowId) => {
                  const row = data[rowId-1];
                  return sum + (parseFloat(row?.lostProduction) || 0);
                }, 0)
                .toFixed(2)
            : cellValue.lostProduction
            ? cellValue.lostProduction
            : Placeholders.DASH,
        [ReferencePeriodTableColumns.CONTRACTUAL_AVAILABILITY]:
          cellValue.referencePeriod === ReferencePeriodTableColumns.REFERENCE_PERIOD_TOTAL
            ? (
                rowsSelected.reduce((sum, rowId) => {
                  const row = data[rowId-1];
                  return sum + (parseFloat(row?.contractualAvailability) || 0);
                }, 0) /
                rowsSelected.reduce((sum, rowId) => {
                  const row = data[rowId-1]?.contractualAvailability > 0 ? 1 : 0;
                  return sum + row;
                }, 0)
              ).toFixed(2)
            : cellValue.contractualAvailability
            ? cellValue.contractualAvailability
            : Placeholders.DASH,
      };
    },
    [data, rowsSelected, t],
  );

  // Table factories
  const [columnGroupFactory, columnFactory, cellFactory] = useTableFactories({
    t,
    columnDefs:
      scopeType === EntityType.CONTRACT
        ? referencePeriodContractTableDefs
        : referencePeriodSiteTableDefs,
    cellValueMapFn,
    customHeaderFn,
    customCellFn,
  });

  if (!ready) {
    return <Loader />;
  }

  return (
    <Container>
      <ReportWidgetTable
        key={name}
        compressed
        noTitles
        scrollable={isInteractive}
        transparent
        columnGroupFactory={columnGroupFactory}
        columnFactory={columnFactory}
        cellFactory={cellFactory}
        columns={columns}
        values={data}
        onValueSelect={() => null}
        rowsSelected={rowsSelected}
        rowKeyProperty={'id'}
        isEditing={isEditing}
        variableHeight={true}
        vAlign={'middle'}
      />
    </Container>
  );
};

ReferencePeriodTableWidget.propTypes = {
  name: PropTypes.string.isRequired,
  data: PropTypes.instanceOf(Object),
  columns: PropTypes.arrayOf(PropTypes.instanceOf(Object)),
  isEditing: PropTypes.bool,
  isInteractive: PropTypes.bool,
  onRowSelectionChange: PropTypes.func.isRequired,
  savedEntries: PropTypes.arrayOf(PropTypes.string),
};
